import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap, ScaleControl } from 'react-leaflet';
import { Box, Typography, Paper, Button, TextField, InputAdornment, IconButton, Tooltip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { OpenStreetMapProvider } from 'leaflet-geosearch';
import type { SearchResult } from 'leaflet-geosearch/dist/providers/provider.js';

// إصلاح أيقونات Leaflet الافتراضية
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

L.Marker.prototype.options.icon = DefaultIcon;

interface MapPickerProps {
  latitude?: number | null;
  longitude?: number | null;
  onChange: (lat: number, lng: number) => void;
  height?: number | string;
}

// مكون داخلي للتعامل مع النقر على الخريطة
function LocationMarker({ onChange }: { onChange: (lat: number, lng: number) => void }) {
  const [position, setPosition] = useState<L.LatLng | null>(null);

  const map = useMapEvents({
    click(e) {
      setPosition(e.latlng);
      onChange(e.latlng.lat, e.latlng.lng);
      map.flyTo(e.latlng, map.getZoom());
    },
  });

  return position === null ? null : (
    <Marker position={position} />
  );
}

// مكون للتحكم في الزوم
function ZoomControls() {
  const map = useMap();
  
  return null; // نستخدم الأزرار الخارجية
}

// مكون لتحريك الخريطة إلى المركز الجديد
function MapUpdater({ center }: { center: [number, number] }) {
  const map = useMap();
  
  useEffect(() => {
    map.flyTo(center, 13, {
      duration: 1.5 // مدة الحركة بالثواني
    });
  }, [center, map]);
  
  return null;
}

// مكون لعرض الإحداثيات على الخريطة
function CoordinatesDisplay() {
  const map = useMap();
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);

  useMapEvents({
    mousemove(e) {
      setCoords({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });

  if (!coords) return null;

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 10,
        left: 10,
        bgcolor: 'rgba(255, 255, 255, 0.9)',
        px: 1.5,
        py: 0.5,
        borderRadius: 1,
        zIndex: 1000,
        fontSize: '12px',
        fontFamily: 'monospace',
        boxShadow: 1,
      }}
    >
      {coords.lat.toFixed(6)}, {coords.lng.toFixed(6)}
    </Box>
  );
}

const MapPicker: React.FC<MapPickerProps> = ({ 
  latitude, 
  longitude, 
  onChange,
  height = 500 
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const mapRef = useRef<L.Map | null>(null);
  const [center, setCenter] = useState<[number, number]>([23.5880, 58.3829]); // مسقط، عمان
  const [selectedPosition, setSelectedPosition] = useState<[number, number] | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult<any>[]>([]);
  const [searching, setSearching] = useState(false);
  const provider = useRef(new OpenStreetMapProvider());

  // تحديث المركز والموضع المحدد عند تغيير القيم
  useEffect(() => {
    if (latitude && longitude) {
      setCenter([latitude, longitude]);
      setSelectedPosition([latitude, longitude]);
    }
  }, [latitude, longitude]);

  const handleLocationChange = (lat: number, lng: number) => {
    const roundedLat = Math.round(lat * 100000000) / 100000000;
    const roundedLng = Math.round(lng * 100000000) / 100000000;
    setSelectedPosition([roundedLat, roundedLng]);
    onChange(roundedLat, roundedLng);
  };

  const handleGetCurrentLocation = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          setCenter([lat, lng]);
          handleLocationChange(lat, lng);
        },
        (error) => {
          console.error('Error getting location:', error);
          alert(t('errorGettingLocation'));
        }
      );
    } else {
      alert(t('geolocationNotSupported'));
    }
  };

  // البحث عن موقع
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      const results = await provider.current.search({ query: searchQuery });
      setSearchResults(results);
      
      if (results.length > 0) {
        const firstResult = results[0];
        const lat = firstResult.y;
        const lng = firstResult.x;
        setCenter([lat, lng]);
        handleLocationChange(lat, lng);
        setSearchQuery(''); // مسح شريط البحث
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearching(false);
    }
  };

  const handleZoomIn = () => {
    if (mapRef.current) {
      mapRef.current.zoomIn();
    }
  };

  const handleZoomOut = () => {
    if (mapRef.current) {
      mapRef.current.zoomOut();
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 2, borderRadius: 2 }}>
      {/* شريط البحث */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          placeholder={t('searchLocation')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          InputProps={{
            startAdornment: !isRTL ? (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#6b7280' }} />
              </InputAdornment>
            ) : undefined,
            endAdornment: (
              <InputAdornment position="end">
                {isRTL && <SearchIcon sx={{ color: '#6b7280', ml: 1 }} />}
                <Button 
                  variant="contained" 
                  size="small"
                  onClick={handleSearch}
                  disabled={searching || !searchQuery.trim()}
                  sx={{ 
                    minWidth: 80,
                    bgcolor: '#3b82f6',
                    '&:hover': { bgcolor: '#2563eb' }
                  }}
                >
                  {searching ? t('searching') : t('search')}
                </Button>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'white',
              '&:hover': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#3b82f6',
                }
              }
            }
          }}
        />
      </Box>

      {/* أزرار التحكم */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Tooltip title={t('useCurrentLocation')} arrow>
          <Button 
            variant="contained" 
            size="small" 
            onClick={handleGetCurrentLocation}
            startIcon={<MyLocationIcon />}
            sx={{ 
              bgcolor: '#10b981',
              '&:hover': { bgcolor: '#059669' }
            }}
          >
            {t('currentLocation')}
          </Button>
        </Tooltip>
        
        <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
          <Tooltip title={t('zoomIn')} arrow>
            <IconButton 
              size="small" 
              onClick={handleZoomIn}
              sx={{ 
                bgcolor: 'white', 
                border: '1px solid #e5e7eb',
                '&:hover': { bgcolor: '#f3f4f6' }
              }}
            >
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('zoomOut')} arrow>
            <IconButton 
              size="small" 
              onClick={handleZoomOut}
              sx={{ 
                bgcolor: 'white', 
                border: '1px solid #e5e7eb',
                '&:hover': { bgcolor: '#f3f4f6' }
              }}
            >
              <ZoomOutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
        💡 {t('clickOnMapToSelectLocation')}
      </Typography>

      <Box sx={{ 
        height, 
        width: '100%', 
        borderRadius: 2, 
        overflow: 'hidden',
        boxShadow: 2,
        position: 'relative',
        border: '2px solid #e5e7eb'
      }}>
        <MapContainer
          center={center}
          zoom={13}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
          ref={mapRef as any}
          zoomControl={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <ScaleControl position="bottomright" imperial={false} />
          <MapUpdater center={center} />
          <LocationMarker onChange={handleLocationChange} />
          <ZoomControls />
          <CoordinatesDisplay />
          {selectedPosition && <Marker position={selectedPosition} />}
        </MapContainer>
      </Box>

      {selectedPosition && (
        <Box sx={{ 
          mt: 2, 
          p: 2, 
          bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
          borderRadius: 2,
          border: '1px solid #0ea5e9'
        }}>
          <Typography variant="body2" color="primary" fontWeight="bold" sx={{ mb: 1 }}>
            📍 {t('selectedCoordinates')}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            <Box sx={{ 
              p: 1.5, 
              bgcolor: 'white', 
              borderRadius: 1,
              textAlign: isRTL ? 'right' : 'left'
            }}>
              <Typography variant="caption" color="text.secondary">
                {t('latitude')}
              </Typography>
              <Typography variant="body1" fontWeight="bold" fontFamily="monospace">
                {selectedPosition[0].toFixed(8)}
              </Typography>
            </Box>
            <Box sx={{ 
              p: 1.5, 
              bgcolor: 'white', 
              borderRadius: 1,
              textAlign: isRTL ? 'right' : 'left'
            }}>
              <Typography variant="caption" color="text.secondary">
                {t('longitude')}
              </Typography>
              <Typography variant="body1" fontWeight="bold" fontFamily="monospace">
                {selectedPosition[1].toFixed(8)}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default MapPicker;
